import { Box, Divider, Typography } from "@mui/material";
import { useState } from "react";
import useColors from "../../../../../../../api/hooks/useColors";
import StatusChip from "../../../../../../../components/chips/StatusChip";
import { RemediationTypeChip } from "../../../../../../../utils/chipsData";
import { ActivityLogType } from "../../../../../../../utils/constants";
import {
  getPrettyDate,
  getReadableTime,
  useLocalTime,
} from "../../../../../../../utils/timestamp";
import { DotAndLine } from "../DotAndLine";
import UndoModal from "./UndoModal";

export default function AutoRemediationDrawerLog({
  auditLog,
  isMostRecentLog,
  showTopConnectLine = true,
  showBottomConnectLine = true,
}) {
  const colors = useColors();
  const showLocalTime = useLocalTime();
  const [showUndoModal, setShowUndoModal] = useState(false);

  const formattedDate = getPrettyDate(
    auditLog.log_timestamp.timestamp_utc,
    showLocalTime
  );
  const formattedTime = getReadableTime(
    auditLog.log_timestamp.timestamp_utc,
    showLocalTime
  );

  return (
    <>
      <Box display="flex" gap={1} height="96px" paddingLeft={0.5}>
        {/* Date and time */}
        <Box display="flex" minWidth="90px" height="56px" alignItems="center">
          <Box
            display="flex"
            flexDirection="column"
            gap={0}
            width="max-content"
            height="max-content"
          >
            <Typography variant="overline" color={colors.text_primary}>
              {formattedTime}
            </Typography>
            <Typography variant="caption" color={colors.text_secondary}>
              {formattedDate}
            </Typography>
          </Box>
        </Box>

        <DotAndLine
          showTop={showTopConnectLine}
          showBottom={showBottomConnectLine}
          topLineHeight={25}
          bottomLineHeight={61}
        />

        <Box
          display="flex"
          width="100%"
          height="min-content"
          padding="16px 8px"
          border={`1px solid ${colors.divider}`}
          borderRadius="12px"
          sx={{ backgroundColor: colors.common_white }}
        >
          <Box
            display="flex"
            width="100%"
            height="54px"
            paddingX="4px"
            gap={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" gap={2}>
              <Box display="flex" flexDirection="column" gap="6px">
                <Typography variant="body2" color={colors.text_secondary}>
                  Approved by
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    maxWidth: 150,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {auditLog.user_display_name}
                </Typography>
              </Box>
              {auditLog.total_affected_entities_in_gap && (
                <>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ borderColor: colors.divider }}
                  />
                  <Box display="flex" flexDirection="column" gap="6px">
                    <Typography variant="body2" color={colors.text_secondary}>
                      Applied on
                    </Typography>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        maxWidth: 150,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {`${auditLog.remediated_entities_count} of ${
                        auditLog.total_affected_entities_in_gap
                      } ${
                        auditLog.total_affected_entities_in_gap === 1
                          ? "entity"
                          : "entities"
                      }`}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
            {/* status and rollback */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-end"
              gap="6px"
            >
              <StatusChip
                circleColor={
                  colors[
                    RemediationTypeChip["autoRemediationLogStatus"][
                      auditLog.log_type
                    ].color + "_dot"
                  ]
                }
                label={
                  RemediationTypeChip["autoRemediationLogStatus"][
                    auditLog.log_type
                  ].label
                }
              />
              {isMostRecentLog &&
                auditLog.log_type !== ActivityLogType.ROLLED_BACK && (
                  <Typography
                    variant="caption"
                    color={colors.primary_enabled_text}
                    sx={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => setShowUndoModal(true)}
                  >
                    Rollback this remediation
                  </Typography>
                )}
            </Box>
          </Box>
        </Box>
      </Box>
      <UndoModal
        showUndoModal={showUndoModal}
        setShowUndoModal={setShowUndoModal}
        log={auditLog}
      />
    </>
  );
}
